import { useEffect, useState } from "react";
import { Card, Form, Button, Row, Col, Table, Spinner } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { useAuth } from "../Context/AuthContext";

import { getTicketDropdownData, addCategory } from "../Api/TicketApiOrg";

const CategoryManagementOrg = () => {
  const { user } = useAuth();
  const orgId = String(user?.organisationId);
  const [loading, setLoading] = useState(false);

  const [dropdownData, setDropdownData] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);

  const [form, setForm] = useState({
    typeGuid: "",
    reasonCode: "",
    categoryName: "",
  });

  const [categoryList, setCategoryList] = useState([]);

  // ==============================
  // Load Dropdown
  // ==============================
  const loadData = async () => {
    try {
      const data = await getTicketDropdownData(orgId);

      setDropdownData(data);

      const types = data.map((t) => ({
        value: t.typeGuid,
        label: t.typeName,
      }));

      setTypeOptions(types);

      // Flatten categories for table
      const allCategories = [];
      data.forEach((t) =>
        t.reasons.forEach((r) =>
          r.categories.forEach((c) =>
            allCategories.push({
              type: t.typeName,
              reason: r.descriptions,
              categoryGuid: c.categoryGuid,
              categoryName: c.categoryName,
            }),
          ),
        ),
      );

      setCategoryList(allCategories);
    } catch {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => {
    if (!orgId) {
      console.log("⛔ No orgId found");
      return;
    }

    loadData();
  }, [orgId]);

  // ==============================
  // Handlers
  // ==============================
  const handleTypeChange = (selected) => {
    const typeGuid = selected?.value;

    setForm({
      ...form,
      typeGuid,
      reasonCode: "",
    });

    const selectedType = dropdownData.find((t) => t.typeGuid === typeGuid);

    if (selectedType) {
      const reasons = selectedType.reasons.map((r) => ({
        value: r.code,
        label: r.descriptions,
      }));

      setReasonOptions(reasons);
    }
  };

  const handleAddCategory = async () => {
    try {
      if (!form.categoryName || !form.reasonCode) {
        toast.error("Fill all fields");
        return;
      }

      setLoading(true);

      await addCategory({
        categoryName: form.categoryName,
        logMapping: form.reasonCode,
        organisationId: orgId,
      });

      toast.success("Category added successfully");

      setForm({
        typeGuid: "",
        reasonCode: "",
        categoryName: "",
      });

      setReasonOptions([]);

      await loadData(); // refresh list
    } catch (error) {
      toast.error(error?.response?.data || "Failed to add category");
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // UI
  // ==============================
  return (
    <div className="container mt-3">
      {/* ================= FORM ================= */}
      <Card className="mb-4">
        <Card.Body>
          <h5>Add Category</h5>

          <Row>
            <Col md={4}>
              <Form.Label>Type</Form.Label>
              <Select
                options={typeOptions}
                value={typeOptions.find((o) => o.value === form.typeGuid)}
                onChange={handleTypeChange}
              />
            </Col>

            <Col md={4}>
              <Form.Label>Reason</Form.Label>
              <Select
                options={reasonOptions}
                value={reasonOptions.find((o) => o.value === form.reasonCode)}
                onChange={(opt) => setForm({ ...form, reasonCode: opt?.value })}
                isDisabled={!form.typeGuid}
              />
            </Col>

            <Col md={4}>
              <Form.Label>Category Name</Form.Label>
              <Form.Control
                value={form.categoryName}
                onChange={(e) =>
                  setForm({ ...form, categoryName: e.target.value })
                }
                placeholder="Enter category"
              />
            </Col>
          </Row>

          <div className="mt-3">
            <Button onClick={handleAddCategory} disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Add Category"}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* ================= LIST ================= */}
      <Card>
        <Card.Body>
          <h5>Category List</h5>

          <Table bordered hover>
            <thead>
              <tr>
                <th>Type</th>
                <th>Reason</th>
                <th>Category</th>
              </tr>
            </thead>
            <tbody>
              {categoryList.map((c, index) => (
                <tr key={index}>
                  <td>{c.type}</td>
                  <td>{c.reason}</td>
                  <td>{c.categoryName}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CategoryManagementOrg;
